import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Loader2, ChevronUp, Sparkles, Building2, Store, Save, Eraser,
  Cloud, CheckCircle2, AlertTriangle, ExternalLink, Copy, Megaphone, Search,
} from 'lucide-react';
import { toast } from 'sonner';
import { CandidatePicker, MailtoButton } from './ContactCandidates';
import { useEnrichmentTracker } from '../../contexts/EnrichmentContext';

const API_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8001'}/api`;

// Indicator fields used to detect whether a lead has enrichment data.
// Two scope-keyed lists below mirror this for the Clear handler.
const SHOP_INDICATOR_FIELDS = ['contact_email', 'contact_phone', 'contact_page_url'];
const COMPANY_INDICATOR_FIELDS = [
  'legal_company_name', 'vat', 'address_street', 'address_number',
  'postal_code', 'city', 'country', 'imprint_url',
];

// P-SF3. The Lightning URL of one record in the connected org. Built only from the
// instance_url the backend validated and an id it returned — never from anything typed here.
const recordUrl = (instanceUrl, id, entityType) => (
  instanceUrl && id ? `${instanceUrl}/lightning/r/${entityType || 'Lead'}/${id}/view` : null
);

// 2.0W. The lists arrive ranked; the head of each is the primary the backend already
// selected, so it starts ticked and the rest do not. Returns the ticked set only — the
// field's own value is never derived from here.
const initialSelection = (candidates, primary) => {
  const values = (candidates || []).map((candidate) => candidate.value);
  if (primary && values.includes(primary)) return [primary];
  // A primary the user typed by hand is not in the list. Nothing is ticked rather than
  // ticking someone else's address on their behalf.
  return primary ? [] : values.slice(0, 1);
};

// M2F. The PHONE picker ticks only the primary itself. The backend now chooses the phone
// primary from the candidates it may promote, so an EMPTY primary means none was eligible — an
// over-cap group, or the merchant's VAT — and ticking the head of the list would imply a number
// the backend refused. Every candidate stays visible and can still be ticked by hand. The email
// picker keeps `initialSelection` above, unchanged.
const initialPhoneSelection = (candidates, primary) => {
  const values = (candidates || []).map((candidate) => candidate.value);
  return primary && values.includes(primary) ? [primary] : [];
};

// LI_A-001. External referent search: two ordinary outbound links the Sales opens by hand.
// CiaoLead fetches, shows and stores nothing from them. Off unless the build sets the flag to
// exactly 'true' or '1'; switching it off is a frontend redeploy.
const liAEnabled = () => ['true', '1'].includes(process.env.REACT_APP_LI_A_ENABLED);

// A static role term per market, keyed by the lead's market as the app stores it (the picker's
// country name) and by its code. Switzerland and every other market get the neutral English
// term: no language is guessed.
const LI_A_ROLE_DEFAULT = 'E-commerce Manager';
const LI_A_ROLES = {
  Germany: 'Leiter E-Commerce', DE: 'Leiter E-Commerce',
  Austria: 'Leiter E-Commerce', AT: 'Leiter E-Commerce',
  France: 'Responsable e-commerce', FR: 'Responsable e-commerce',
  Spain: 'Responsable ecommerce', ES: 'Responsable ecommerce',
};
export const liARole = (market) => (
  Object.prototype.hasOwnProperty.call(LI_A_ROLES, market) ? LI_A_ROLES[market] : LI_A_ROLE_DEFAULT
);

// The search-result pages, never a profile. Built from the merchant's commercial name and the
// static role only, each URL-encoded whole. An empty name yields no links at all.
// LI_A-007: the company search takes the name alone — no role, no domain — since a company page
// may carry a trade or legal name that differs from the shop's website.
export const liALinks = (shopName, market) => {
  const name = (shopName || '').trim();
  if (!name) return null;
  const role = liARole(market);
  return {
    linkedin: `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent(`${name} ${role}`)}`,
    linkedinCompany: `https://www.linkedin.com/search/results/companies/?keywords=${encodeURIComponent(name)}`,
    google: `https://www.google.com/search?q=${encodeURIComponent(`"${name}" ${role} site:linkedin.com/in`)}`,
  };
};

const LI_A_NOTICE = 'Opens an external search. CiaoLead does not save results. Follow local outreach rules.';

// Enrichment Panel Component for approved leads
export const EnrichmentPanel = ({
  lead, token, onClose, onEnrichmentChanged, onEnrichmentCleared, onDirtyChange,
}) => {
  // eslint-disable-next-line no-unused-vars
  const [enrichment, setEnrichment] = useState(null);
  const [loading, setLoading] = useState(true);
  // 2.0X — the run is NOT owned here. The panel reads whether this lead is enriching and
  // asks the owner to start one; closing the panel therefore abandons nothing.
  const { jobs, isEnriching, startEnrichment, takeResult } = useEnrichmentTracker();
  const enriching = isEnriching(lead.id);
  const job = jobs[lead.id];
  const [saving, setSaving] = useState(false);
  const [shopData, setShopData] = useState({});
  const [companyData, setCompanyData] = useState({});
  // 2.0W. The candidate lists exactly as the backend ranked them — never re-sorted or
  // re-filtered here; that order IS the ranking. `selected*` is composing state only: it
  // never touches shopData, so ticking a row cannot edit the lead.
  const [emailCandidates, setEmailCandidates] = useState([]);
  const [phoneCandidates, setPhoneCandidates] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectedPhones, setSelectedPhones] = useState([]);
  // P4.8c: Clear Enrichment state
  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  // P-SF3. `salesforce` is null while unknown, the string 'unavailable' when the API does not
  // offer the integration (404 — the flag is off), and otherwise the state the backend holds
  // for this lead.
  const [salesforce, setSalesforce] = useState(null);
  // P-SF3. True from the first edit until the fields are the persisted ones again — a
  // successful Save, a fresh enrichment result, or a reload. The Salesforce create builds its
  // payload from what is PERSISTED, so while this is true the create is refused rather than
  // sending stale values or saving on the user's behalf.
  const [dirty, setDirty] = useState(false);
  const [creating, setCreating] = useState(false);
  // A disabled button is not proof: two clicks can reach the handler before React re-renders,
  // and a second POST is exactly what must never happen.
  const inFlight = useRef(false);
  // P-SF4. The org's ACTIVE Campaigns: null while unknown, the string 'unavailable' when the
  // list could not be read, otherwise the array. Not being able to read it never touches
  // the Lead create — the create simply runs without a Campaign.
  const [campaigns, setCampaigns] = useState(null);
  // The Campaign chosen for the NEXT action: create-then-add, or Add to campaign. '' is none.
  // Deliberately outside shopData/companyData — choosing one is not an edit of the lead,
  // marks nothing dirty and saves nothing.
  const [campaignId, setCampaignId] = useState('');
  // What CiaoLead recorded about this lead's Campaigns, newest first. Loaded once with the
  // lead's state and then updated only by this panel's own membership calls.
  const [memberships, setMemberships] = useState([]);
  const [addingCampaign, setAddingCampaign] = useState(false);
  const membershipInFlight = useRef(false);

  // P-SF3. The only way the two edited sections change from the form.
  const editShop = (patch) => {
    setShopData((current) => ({ ...current, ...patch }));
    setDirty(true);
  };
  const editCompany = (patch) => {
    setCompanyData((current) => ({ ...current, ...patch }));
    setDirty(true);
  };

  // P-SF5. A bulk send builds its payload from what is PERSISTED, exactly as the single
  // create does, so a lead whose panel holds unsaved edits must be held back rather than
  // sent with values the user is not looking at. Only the FACT is shared; the form state
  // stays here, where it belongs.
  useEffect(() => {
    if (onDirtyChange) onDirtyChange(lead.id, dirty);
  }, [dirty, lead.id, onDirtyChange]);

  // Closing the panel throws those edits away with it, so the lead is clean again.
  useEffect(() => () => {
    if (onDirtyChange) onDirtyChange(lead.id, false);
  }, [lead.id, onDirtyChange]);

  // LI_A-001. The commercial name as persisted (the enrichment card's shop name, else the
  // lead's own trade name) — never the unsaved input, the legal name or a person. Approved
  // leads only, as the Dashboard already mounts this panel.
  const externalSearch = liAEnabled() && lead.decision === 'approved'
    ? liALinks((enrichment?.shop?.shop_name?.value || '').trim() || lead.shop_name, lead.country)
    : null;

  // Derived: does this lead currently hold any indicator field value (in local state)?
  const hasAnyIndicator =
    SHOP_INDICATOR_FIELDS.some(f => (shopData[f] || '').trim()) ||
    COMPANY_INDICATOR_FIELDS.some(f => (companyData[f] || '').trim());

  // P4.8c: UI-only clear of enrichment fields. No backend call.
  const handleClearConfirm = () => {
    setShopData(prev => {
      const next = { ...prev };
      SHOP_INDICATOR_FIELDS.forEach(f => { next[f] = ''; });
      return next;
    });
    setCompanyData(prev => {
      const next = { ...prev };
      COMPANY_INDICATOR_FIELDS.forEach(f => { next[f] = ''; });
      return next;
    });
    setClearDialogOpen(false);
    // P-SF3: clearing is an unsaved edit like any other, so the panel is now dirty and
    // Create in Salesforce is refused until the user saves (or reloads).
    setDirty(true);
    toast.info('Enrichment data cleared (UI only — not saved)');
    if (typeof onEnrichmentCleared === 'function') {
      onEnrichmentCleared(lead.id);
    }
  };

  // Load enrichment data
  // 2.0W. Ticking is additive and independent per field; nothing here writes shopData.
  const toggleSelection = (setSelected) => (value) =>
    setSelected((current) => (current.includes(value)
      ? current.filter((item) => item !== value)
      : [...current, value]));

  useEffect(() => {
    const loadEnrichment = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/leads/${lead.id}/enrichment`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setEnrichment(response.data);
        setShopData({
          shop_name: response.data.shop?.shop_name?.value || '',
          website: response.data.shop?.website?.value || '',
          contact_email: response.data.shop?.contact_email?.value || '',
          contact_phone: response.data.shop?.contact_phone?.value || '',
          contact_page_url: response.data.shop?.contact_page_url?.value || '',
        });
        const emails = response.data.shop?.contact_email_candidates || [];
        const phones = response.data.shop?.contact_phone_candidates || [];
        setEmailCandidates(emails);
        setPhoneCandidates(phones);
        setSelectedEmails(
          initialSelection(emails, response.data.shop?.contact_email?.value));
        setSelectedPhones(
          initialPhoneSelection(phones, response.data.shop?.contact_phone?.value));
        const company = {
          legal_company_name: response.data.company_data?.legal_company_name?.value || '',
          vat: response.data.company_data?.vat?.value || '',
          address_street: response.data.company_data?.address_street?.value || '',
          address_number: response.data.company_data?.address_number?.value || '',
          postal_code: response.data.company_data?.postal_code?.value || '',
          city: response.data.company_data?.city?.value || '',
          country: response.data.company_data?.country?.value || '',
          imprint_url: response.data.company_data?.imprint_url?.value || '',
        };
        setCompanyData(company);
        setDirty(false);              // P-SF3: these ARE the persisted values
      } catch (error) {
        toast.error('Failed to load enrichment data');
      } finally {
        setLoading(false);
      }
    };
    loadEnrichment();
  }, [lead.id, token]);

  // P-SF3. What CiaoLead knows about this lead in Salesforce. Anything other than a clean
  // answer — the integration is off (404), the account may not read it, the request failed —
  // hides every Salesforce control rather than guessing.
  // P-SF4. The org's active Campaigns, for the picker. A list that cannot be read is
  // reported as such and never disables anything: a Campaign is optional.
  const loadCampaigns = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/salesforce/campaigns`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setCampaigns(Array.isArray(response.data?.campaigns) ? response.data.campaigns : []);
    } catch (error) {
      setCampaigns('unavailable');
    }
  }, [token]);

  // P-SF4. What CiaoLead recorded about this lead's Campaigns. Only a created lead has any.
  const loadMemberships = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/salesforce/leads/${lead.id}/campaign-memberships`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setMemberships(Array.isArray(response.data?.memberships) ? response.data.memberships : []);
    } catch (error) {
      // The Lead's own state is on screen regardless; an unreadable membership list hides
      // nothing about the Lead, so it stays empty until this panel's next action fills it.
      setMemberships([]);
    }
  }, [lead.id, token]);

  const loadSalesforce = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/salesforce/leads/${lead.id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSalesforce(response.data);
      const leadState = response.data?.state;
      // P-SF4. Campaign controls exist only where a Campaign can be chosen: before a create,
      // or for a created lead. A duplicate or an unknown creation offers none, so nothing is
      // read for them.
      if (response.data?.connection_status === 'connected'
          && ['ready', 'failed', 'created'].includes(leadState)) {
        loadCampaigns();
      }
      if (leadState === 'created') await loadMemberships();
    } catch (error) {
      setSalesforce('unavailable');
    }
  }, [lead.id, token, loadCampaigns, loadMemberships]);

  useEffect(() => { loadSalesforce(); }, [loadSalesforce]);

  // 2.0X — start it and return. The owner holds the request, notifies on completion and
  // keeps the result for whoever is still mounted; this panel may be gone by then.
  const handleEnrich = () => startEnrichment(lead);

  // Applies one enrichment payload to the fields, whether it arrived from the initial GET,
  // from a run that finished while this panel was open, or from one that finished while it
  // was closed. One reader, so the three paths cannot drift apart.
  const applyEnrichment = useCallback((data) => {
    if (!data) return;
    setEnrichment(data);
    setShopData((current) => ({
      shop_name: data.shop?.shop_name?.value || current.shop_name,
      website: data.shop?.website?.value || current.website,
      contact_email: data.shop?.contact_email?.value || current.contact_email,
      contact_phone: data.shop?.contact_phone?.value || current.contact_phone,
      contact_page_url: data.shop?.contact_page_url?.value || current.contact_page_url,
    }));
    const freshEmails = data.shop?.contact_email_candidates || [];
    const freshPhones = data.shop?.contact_phone_candidates || [];
    setEmailCandidates(freshEmails);
    setPhoneCandidates(freshPhones);
    setSelectedEmails(initialSelection(freshEmails, data.shop?.contact_email?.value));
    setSelectedPhones(initialPhoneSelection(freshPhones, data.shop?.contact_phone?.value));
    setCompanyData((current) => ({
      legal_company_name: data.company_data?.legal_company_name?.value
        || current.legal_company_name,
      vat: data.company_data?.vat?.value || current.vat,
      address_street: data.company_data?.address_street?.value || current.address_street,
      address_number: data.company_data?.address_number?.value || current.address_number,
      postal_code: data.company_data?.postal_code?.value || current.postal_code,
      city: data.company_data?.city?.value || current.city,
      country: data.company_data?.country?.value || current.country,
      imprint_url: data.company_data?.imprint_url?.value || current.imprint_url,
    }));
    // P-SF3. `dirty` is deliberately NOT cleared here. This runs for a result that arrived on
    // its own — a background enrichment finishing — and the merge above keeps the user's own
    // value wherever the result has none, so clearing would mark a form clean while unsaved
    // edits are still on screen, and the Salesforce create would then send the DIFFERENT
    // values that are persisted. Only the initial load and a successful Save clear it.
    if (typeof onEnrichmentChanged === 'function') {
      onEnrichmentChanged(lead.id);
    }
  }, [lead.id, onEnrichmentChanged]);

  // A finished result is collected whether the panel watched the run or was reopened after
  // it. `takeResult` hands it over once, so reopening later falls back to the ordinary GET
  // instead of replaying a stale payload over fields the user may have edited since.
  useEffect(() => {
    if (job?.status !== 'done') return;
    const data = takeResult(lead.id);
    if (data) {
      applyEnrichment(data);
      setLoading(false);
    }
  }, [job?.status, lead.id, takeResult, applyEnrichment]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const now = new Date().toISOString();
      await axios.put(
        `${API_URL}/leads/${lead.id}/enrichment`,
        {
          shop: {
            shop_name: { value: shopData.shop_name, updated_at: now },
            website: { value: shopData.website, updated_at: now },
            contact_email: { value: shopData.contact_email, updated_at: now },
            contact_phone: { value: shopData.contact_phone, updated_at: now },
            contact_page_url: { value: shopData.contact_page_url, updated_at: now },
          },
          company_data: {
            legal_company_name: { value: companyData.legal_company_name, updated_at: now },
            vat: { value: companyData.vat, updated_at: now },
            address_street: { value: companyData.address_street, updated_at: now },
            address_number: { value: companyData.address_number, updated_at: now },
            postal_code: { value: companyData.postal_code, updated_at: now },
            city: { value: companyData.city, updated_at: now },
            country: { value: companyData.country, updated_at: now },
            imprint_url: { value: companyData.imprint_url, updated_at: now },
          }
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // P-SF3. What is on screen is now what is persisted, so the panel is clean and the
      // Salesforce create is offered again. A Save that THREW never reaches this line, so a
      // failed save stays dirty.
      setDirty(false);
      toast.success('Data saved');
      // P4.8a-1: manual edits may have introduced indicator-field values (e.g. contact_email),
      // so the enrichment badge needs to refresh too.
      if (typeof onEnrichmentChanged === 'function') {
        onEnrichmentChanged(lead.id);
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  // P-SF4. Which name to show for a Campaign the backend has not named yet. Display only,
  // from the list the org itself gave us; it is never sent anywhere.
  const campaignName = (id) => {
    const found = Array.isArray(campaigns) ? campaigns.find((c) => c.id === id) : null;
    return found ? found.name : '';
  };

  const rememberMembership = (membership) => setMemberships((current) => [
    membership, ...current.filter((m) => m.campaign_id !== membership.campaign_id),
  ]);

  // P-SF4. The one Campaign write. It never creates the Lead: the backend refuses anything
  // but a created lead, and this only ever runs for one. A Retry calls this and nothing else.
  // No enrichment field travels with it, so unsaved edits neither block it nor get saved.
  const addToCampaign = async (id) => {
    if (!id || membershipInFlight.current) return;
    membershipInFlight.current = true;
    setAddingCampaign(true);
    try {
      const response = await axios.post(
        `${API_URL}/salesforce/leads/${lead.id}/campaign-memberships`,
        { campaign_id: id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      rememberMembership(response.data);
      const name = response.data?.campaign_name || campaignName(id) || 'the campaign';
      const outcome = response.data?.state;
      if (outcome === 'added') toast.success(`Added to ${name}`);
      else if (outcome === 'already_member') toast.success(`Already in ${name}`);
      else if (outcome === 'failed') toast.error('Campaign not associated');
    } catch (error) {
      // Refused before anything was written (a 409, a 422), or not answered at all. Shown as
      // a failed association with its reason and a Retry; the Lead is not involved.
      const code = error?.response?.data?.detail;
      rememberMembership({
        lead_id: lead.id, campaign_id: id, campaign_name: campaignName(id) || null,
        state: 'failed', error_code: typeof code === 'string' ? code : 'request_failed',
        salesforce_campaign_member_id: null, instance_url: null, updated_at: null,
      });
      toast.error('Campaign not associated');
    } finally {
      membershipInFlight.current = false;
      setAddingCampaign(false);
    }
  };

  // P-SF3. The one write. The backend builds the payload from the PERSISTED lead and
  // enrichment and answers with the state it stored, so this only reports what happened.
  const createInSalesforce = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setCreating(true);
    try {
      const response = await axios.post(
        `${API_URL}/salesforce/leads/${lead.id}`,
        null,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setSalesforce(response.data);
      const outcome = response.data?.state;
      if (outcome === 'created') toast.success('Created in Salesforce');
      else if (outcome === 'duplicate') toast.warning('Salesforce found a possible duplicate');
      else if (outcome === 'creation_unknown') {
        toast.warning('Creation status unknown — check Salesforce');
      } else if (outcome === 'failed') toast.error('Salesforce did not accept this lead');
      if (outcome === 'created' && campaignId) {
        // P-SF4. The Lead is created: that fact is stored and already on screen. The
        // Campaign is a SECOND, independent write, and only a created lead gets one — a
        // duplicate, a failure or an unknown outcome sends nothing. Whatever happens to it,
        // the line above stands.
        await addToCampaign(campaignId);
      }
    } catch (error) {
      // The connection may have gone while the panel was open. Re-read rather than guess.
      await loadSalesforce();
      toast.error('Could not create in Salesforce');
    } finally {
      inFlight.current = false;
      setCreating(false);
    }
  };

  const salesforceReady = salesforce && salesforce !== 'unavailable';
  const connectionBlocked = salesforceReady && salesforce.connection_status !== 'connected';
  const state = salesforceReady ? salesforce.state : null;
  const busy = creating || state === 'creating';
  // P-SF4.
  const campaignsKnown = Array.isArray(campaigns) && campaigns.length > 0;
  const campaignsUnavailable = campaigns === 'unavailable';
  const selectedHasRecord = memberships.some((m) => m.campaign_id === campaignId);

  // P-SF4. A user-facing reason for a failed association. Codes only ever come from the
  // backend's fixed vocabulary; one this list does not know is shown as itself.
  const membershipReason = (code) => ({
    salesforce_campaign_unavailable: 'This campaign is no longer available in Salesforce.',
    salesforce_campaign_inactive: 'This campaign is no longer active.',
    salesforce_connection_changed:
      'Salesforce is now connected to a different org than the one this lead was created in.',
    salesforce_unreachable: 'Salesforce did not answer. Try again.',
    salesforce_server_error: 'Salesforce did not answer. Try again.',
    salesforce_reauth_required:
      'Salesforce needs to be authorised again. An administrator can do that in Settings.',
    salesforce_not_connected: 'Salesforce is not connected.',
    result_not_persisted: 'The result could not be recorded. Try again.',
    INSUFFICIENT_ACCESS_ON_CROSS_REFERENCE_ENTITY:
      'The connected Salesforce user cannot add members to this campaign.',
    lead_not_created_in_salesforce: 'The lead has not been created in Salesforce.',
  }[code] || (code
    ? `Salesforce did not accept the association (${code}).`
    : 'Salesforce did not accept the association.'));

  // Name first; status and start date only when the org gave them, as secondary context.
  const campaignLabel = (campaign) => [
    campaign.name, campaign.status, campaign.start_date ? `from ${campaign.start_date}` : null,
  ].filter(Boolean).join(' · ');

  // P-SF4. The optional Campaign. Absent entirely while the org has no active Campaign, so
  // the row looks exactly as it did before this existed. An unreadable list says so and
  // offers a retry — and never disables the Create beside it.
  const renderCampaignPicker = () => {
    if (campaignsUnavailable) {
      return (
        <span className="text-sm text-slate-400" data-testid={`salesforce-campaigns-unavailable-${lead.id}`}>
          Campaigns unavailable.{' '}
          <button
            type="button"
            onClick={loadCampaigns}
            className="underline hover:text-slate-200"
            data-testid={`salesforce-campaigns-retry-${lead.id}`}
          >
            Retry
          </button>
        </span>
      );
    }
    if (!campaignsKnown) return null;
    return (
      <label className="flex items-center gap-2 text-xs text-slate-400">
        Campaign (optional)
        <select
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
          disabled={busy || addingCampaign}
          className="h-8 rounded-md border border-white/15 bg-transparent px-2 text-sm text-slate-200 focus:outline-none focus:ring-1 focus:ring-blue-400/60 disabled:opacity-50"
          data-testid={`salesforce-campaign-select-${lead.id}`}
        >
          <option value="">No campaign</option>
          {campaigns.map((campaign) => (
            <option key={campaign.id} value={campaign.id}>{campaignLabel(campaign)}</option>
          ))}
        </select>
      </label>
    );
  };

  // P-SF4. One membership row. A success is a success whichever way it came — added now,
  // or already there; a failure names its reason and offers to try the membership again,
  // and ONLY the membership.
  const renderMembership = (membership) => {
    const name = membership.campaign_name || campaignName(membership.campaign_id) || '';
    const rowId = `salesforce-membership-${lead.id}-${membership.campaign_id}`;
    if (membership.state === 'added' || membership.state === 'already_member') {
      const link = recordUrl(membership.instance_url, membership.campaign_id, 'Campaign');
      return (
        <div key={membership.campaign_id} className="flex flex-wrap items-center gap-3" data-testid={rowId} data-state={membership.state}>
          <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-300">
            <CheckCircle2 className="h-3 w-3" />
            {`${membership.state === 'added' ? 'Added to' : 'Already in'} ${name || 'campaign'}`}
          </span>
          {link && (
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-300 hover:text-blue-200 underline"
              data-testid={`salesforce-open-campaign-${lead.id}-${membership.campaign_id}`}
            >
              Open campaign
              <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      );
    }
    if (membership.state === 'adding') {
      return (
        <div key={membership.campaign_id} className="flex items-center gap-2 text-sm text-slate-400" data-testid={rowId} data-state="adding">
          <Loader2 className="h-4 w-4 animate-spin" />
          {`Adding to ${name || 'campaign'}...`}
        </div>
      );
    }
    return (
      <div key={membership.campaign_id} className="space-y-1" data-testid={rowId} data-state="failed">
        <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-300">
          <AlertTriangle className="h-3 w-3" />
          {`Campaign not associated${name ? ` (${name})` : ''}`}
        </span>
        <p className="text-sm text-slate-400">{membershipReason(membership.error_code)}</p>
        <Button
          size="sm"
          variant="outline"
          onClick={() => addToCampaign(membership.campaign_id)}
          disabled={addingCampaign}
          className="h-7 border-white/15 text-slate-200 hover:bg-white/10"
          data-testid={`salesforce-membership-retry-${lead.id}-${membership.campaign_id}`}
        >
          {addingCampaign && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
          Retry
        </Button>
      </div>
    );
  };

  const renderSalesforce = () => {
    if (state === 'created') {
      const link = recordUrl(salesforce.instance_url, salesforce.salesforce_record_id, 'Lead');
      return (
        <div className="space-y-3" data-testid={`salesforce-created-${lead.id}`}>
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-300">
              <CheckCircle2 className="h-3 w-3" />
              Created in Salesforce
            </span>
            {link && (
              <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-blue-300 hover:text-blue-200 underline"
                data-testid={`salesforce-open-${lead.id}`}
              >
                Open in Salesforce
                <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
          {memberships.map(renderMembership)}
          {/* P-SF4. A created lead may still be routed, now or later. This sends no
              enrichment field, so unsaved edits do not block it and nothing is saved on the
              user's behalf — and it never creates the Lead again. */}
          {!connectionBlocked && (campaignsKnown || campaignsUnavailable) && (
            <div className="flex flex-wrap items-center gap-3">
              {renderCampaignPicker()}
              {campaignsKnown && (
                <Button
                  size="sm"
                  onClick={() => addToCampaign(campaignId)}
                  disabled={!campaignId || addingCampaign || selectedHasRecord}
                  className="h-8 world-btn-secondary"
                  data-testid={`salesforce-add-campaign-btn-${lead.id}`}
                >
                  {addingCampaign ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Megaphone className="h-4 w-4 mr-1" />
                  )}
                  {addingCampaign ? 'Adding...' : 'Add to campaign'}
                </Button>
              )}
            </div>
          )}
        </div>
      );
    }

    if (state === 'duplicate') {
      return (
        <div className="space-y-2" data-testid={`salesforce-duplicate-${lead.id}`}>
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-300">
            <Copy className="h-3 w-3" />
            Potential duplicate found in Salesforce
          </span>
          <p className="text-sm text-slate-400">
            Salesforce did not create this lead. Review the matching record there; merging stays
            in Salesforce.
          </p>
          <div className="flex flex-wrap gap-3">
            {(salesforce.matches || []).map((match) => {
              const link = recordUrl(salesforce.instance_url, match.id, match.entity_type);
              return link ? (
                <a
                  key={match.id}
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-blue-300 hover:text-blue-200 underline"
                  data-testid={`salesforce-match-${lead.id}-${match.id}`}
                >
                  {`Open ${match.entity_type || 'Lead'} in Salesforce`}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : null;
            })}
          </div>
        </div>
      );
    }

    if (state === 'creation_unknown') {
      return (
        <div className="space-y-1" data-testid={`salesforce-unknown-${lead.id}`}>
          <span className="inline-flex items-center gap-1 rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-xs text-amber-300">
            <AlertTriangle className="h-3 w-3" />
            Creation status unknown — check Salesforce before trying again.
          </span>
          <p className="text-sm text-slate-400">
            CiaoLead could not confirm whether the lead was created, so it will not try again.
          </p>
        </div>
      );
    }

    if (connectionBlocked) {
      return (
        <p className="text-sm text-slate-400" data-testid={`salesforce-settings-hint-${lead.id}`}>
          {salesforce.connection_status === 'reauth_required'
            ? 'Salesforce needs to be authorised again. An administrator can do that in Settings.'
            : 'Salesforce is not connected. An administrator can connect it in Settings.'}
        </p>
      );
    }

    return (
      <div className="flex flex-wrap items-center gap-3">
        {/* P-SF4. Chosen here, applied only AFTER the Lead comes back created. */}
        {renderCampaignPicker()}
        <Button
          size="sm"
          onClick={createInSalesforce}
          disabled={busy || dirty}
          className="h-8 world-btn-primary"
          data-testid={`salesforce-create-btn-${lead.id}`}
        >
          {busy ? (
            <Loader2 className="h-4 w-4 mr-1 animate-spin" />
          ) : (
            <Cloud className="h-4 w-4 mr-1" />
          )}
          {busy ? 'Creating...' : (state === 'failed' ? 'Retry in Salesforce' : 'Create in Salesforce')}
        </Button>
        {state === 'failed' && (
          <span className="text-sm text-amber-300" data-testid={`salesforce-failed-${lead.id}`}>
            Salesforce did not accept this lead
            {salesforce.error_code ? ` (${salesforce.error_code})` : ''}.
          </span>
        )}
        {dirty && (
          <span className="text-sm text-slate-400" data-testid={`salesforce-dirty-${lead.id}`}>
            Save changes before creating in Salesforce.
          </span>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <tr>
        <td colSpan="7" className="px-6 py-8 bg-white/5">
          <div className="flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
            <span className="ml-2 text-slate-400">Loading enrichment data...</span>
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td colSpan="7" className="px-6 py-4 bg-white/5 border-t border-white/10">
        <div className="space-y-4">
          {/* Header with Enrich button */}
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-white">Lead Details</h4>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleEnrich}
                disabled={enriching}
                className="h-8 world-btn-primary"
                data-testid={`enrich-btn-${lead.id}`}
              >
                {enriching ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Sparkles className="h-4 w-4 mr-1" />
                )}
                {enriching ? 'Enriching...' : 'Enrich'}
              </Button>
              <Button
                variant="default"
                size="sm"
                onClick={handleSave}
                disabled={saving}
                className="h-8 world-btn-secondary"
                data-testid={`save-btn-${lead.id}`}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-1" />
                )}
                Save
              </Button>
              {/* P4.8c: Clear Enrichment — visible only when at least one indicator field has a value */}
              {hasAnyIndicator && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setClearDialogOpen(true)}
                  className="h-8 border-red-500/40 text-red-400 hover:bg-red-500/15"
                  data-testid={`clear-enrichment-btn-${lead.id}`}
                >
                  <Eraser className="h-4 w-4 mr-1" />
                  Clear Enrichment
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                className="h-8 px-2"
              >
                <ChevronUp className="h-4 w-4" />
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Shop Section */}
            <div className="world-panel p-4">
              <div className="flex items-center gap-2 mb-4">
                <Store className="h-5 w-5 text-blue-400" />
                <h5 className="font-medium text-white">Shop Info</h5>
                {externalSearch && (
                  <div className="ml-auto">
                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          title={LI_A_NOTICE}
                          data-testid={`li-a-trigger-${lead.id}`}
                        >
                          <Search className="h-3 w-3 mr-1" />
                          Find e-commerce contact
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-64" data-testid={`li-a-menu-${lead.id}`}>
                        <DropdownMenuLabel className="text-xs font-normal text-muted-foreground">
                          {LI_A_NOTICE}
                        </DropdownMenuLabel>
                        <DropdownMenuItem asChild>
                          <a
                            href={externalSearch.google}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`li-a-google-${lead.id}`}
                          >
                            <ExternalLink className="h-3 w-3 mr-2" />
                            Google — Find contact
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a
                            href={externalSearch.linkedin}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`li-a-linkedin-${lead.id}`}
                          >
                            <ExternalLink className="h-3 w-3 mr-2" />
                            LinkedIn — Find people
                          </a>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <a
                            href={externalSearch.linkedinCompany}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid={`li-a-linkedin-company-${lead.id}`}
                          >
                            <ExternalLink className="h-3 w-3 mr-2" />
                            LinkedIn — Find company
                          </a>
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-400">Shop Name</Label>
                  <Input
                    value={shopData.shop_name}
                    onChange={(e) => editShop({ shop_name: e.target.value })}
                    className="h-9"
                    data-testid={`shop-name-${lead.id}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Website</Label>
                  <Input
                    value={shopData.website}
                    onChange={(e) => editShop({ website: e.target.value })}
                    className="h-9"
                    data-testid={`shop-website-${lead.id}`}
                  />
                </div>
                <div>
                  {/* 2.0W. The controls live on the LABEL row, so the input below is
                      untouched and a field with fewer than two candidates renders exactly
                      as it did before. `min-h-5` holds that row's height either way, so
                      nothing moves when a dropdown appears or disappears. */}
                  <div className="flex min-h-5 items-center justify-between gap-2">
                    <Label className="text-xs text-slate-400">Contact Email</Label>
                    <div className="flex items-center gap-2">
                      <CandidatePicker
                        candidates={emailCandidates}
                        selected={selectedEmails}
                        onToggle={toggleSelection(setSelectedEmails)}
                        label="Addresses found for this merchant"
                        testId={`shop-email-candidates-${lead.id}`}
                        // The primary IS the field: it is read from contact_email and
                        // written back through the ordinary edit path, so choosing one
                        // marks the form dirty and still waits for Save. Ticking, beside
                        // it, keeps composing the mail and touches none of this.
                        primaryValue={shopData.contact_email}
                        onPrimaryChange={(value) => editShop({ contact_email: value })}
                      />
                      <MailtoButton
                        addresses={selectedEmails}
                        testId={`shop-email-mailto-${lead.id}`}
                      />
                    </div>
                  </div>
                  <Input
                    value={shopData.contact_email}
                    onChange={(e) => editShop({ contact_email: e.target.value })}
                    placeholder="e.g. info@shop.com"
                    className="h-9"
                    data-testid={`shop-email-${lead.id}`}
                  />
                </div>
                <div>
                  <div className="flex min-h-5 items-center justify-between gap-2">
                    <Label className="text-xs text-slate-400">Contact Phone</Label>
                    <CandidatePicker
                      candidates={phoneCandidates}
                      selected={selectedPhones}
                      onToggle={toggleSelection(setSelectedPhones)}
                      label="Numbers found for this merchant"
                      testId={`shop-phone-candidates-${lead.id}`}
                    />
                  </div>
                  <Input
                    value={shopData.contact_phone}
                    onChange={(e) => editShop({ contact_phone: e.target.value })}
                    className="h-9"
                    data-testid={`shop-phone-${lead.id}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Contact Page URL</Label>
                  <Input
                    value={shopData.contact_page_url}
                    onChange={(e) => editShop({ contact_page_url: e.target.value })}
                    className="h-9"
                    data-testid={`shop-contact-url-${lead.id}`}
                  />
                </div>
              </div>
            </div>

            {/* Company/Debtor Section */}
            <div className="world-panel p-4">
              <div className="flex items-center gap-2 mb-4">
                <Building2 className="h-5 w-5 text-purple-600" />
                <h5 className="font-medium text-white">Company (Debtor)</h5>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-400">Legal Company Name</Label>
                  <Input
                    value={companyData.legal_company_name}
                    onChange={(e) => editCompany({ legal_company_name: e.target.value })}
                    className="h-9"
                    data-testid={`company-name-${lead.id}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">VAT Number</Label>
                  <Input
                    value={companyData.vat}
                    onChange={(e) => editCompany({ vat: e.target.value })}
                    placeholder="e.g. IT12345678901"
                    className="h-9"
                    data-testid={`company-vat-${lead.id}`}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="col-span-2">
                    <Label className="text-xs text-slate-400">Street</Label>
                    <Input
                      value={companyData.address_street}
                      onChange={(e) => editCompany({ address_street: e.target.value })}
                      className="h-9"
                      data-testid={`company-street-${lead.id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">Number</Label>
                    <Input
                      value={companyData.address_number}
                      onChange={(e) => editCompany({ address_number: e.target.value })}
                      className="h-9"
                      data-testid={`company-number-${lead.id}`}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs text-slate-400">Postal Code</Label>
                    <Input
                      value={companyData.postal_code}
                      onChange={(e) => editCompany({ postal_code: e.target.value })}
                      className="h-9"
                      data-testid={`company-postal-${lead.id}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-slate-400">City</Label>
                    <Input
                      value={companyData.city}
                      onChange={(e) => editCompany({ city: e.target.value })}
                      className="h-9"
                      data-testid={`company-city-${lead.id}`}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Country</Label>
                  <Input
                    value={companyData.country}
                    onChange={(e) => editCompany({ country: e.target.value })}
                    className="h-9"
                    data-testid={`company-country-${lead.id}`}
                  />
                </div>
                <div>
                  <Label className="text-xs text-slate-400">Imprint URL</Label>
                  <Input
                    value={companyData.imprint_url}
                    onChange={(e) => editCompany({ imprint_url: e.target.value })}
                    className="h-9"
                    data-testid={`company-imprint-${lead.id}`}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* P-SF3: Salesforce. Absent entirely while the integration is off. */}
          {salesforceReady && (
            <div className="world-panel p-4" data-testid={`salesforce-section-${lead.id}`}>
              <div className="flex items-center gap-2 mb-3">
                <Cloud className="h-5 w-5 text-blue-400" />
                <h5 className="font-medium text-white">Salesforce</h5>
              </div>
              {renderSalesforce()}
            </div>
          )}

          {/* P4.8c: Clear Enrichment confirmation dialog */}
          <AlertDialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
            <AlertDialogContent data-testid={`clear-enrichment-dialog-${lead.id}`}>
              <AlertDialogHeader>
                <AlertDialogTitle>Clear enrichment data for this lead?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will reset the enriched fields shown here (contact details, VAT, address...).
                  This change is UI-only and will not be saved — refreshing the page will restore the data.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid={`clear-enrichment-cancel-${lead.id}`}>
                  Cancel
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleClearConfirm}
                  className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
                  data-testid={`clear-enrichment-confirm-${lead.id}`}
                >
                  Clear Enrichment
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </td>
    </tr>
  );
};
